import type { NavigateFunction } from 'react-router-dom';
import type { CompiledPublicTile } from '@shared/types/domainConfig';
import { routeForModule } from '@shared/lib/moduleRoutes';
import { openLink } from '@shared/lib/openLink';
import { platform } from '@shared/lib/platform';

/**
 * A tile that opens the Panic module — rendered as a white "SOS" on a red disc
 * (instead of icon + label) on the home grid/rail and the Estate tile grid.
 */
export function isPanicTile(tile: CompiledPublicTile): boolean {
  return (
    tile.module_required === 'panic' ||
    (tile.action_type === 'module' && tile.action_config?.module_route === '/app/panic')
  );
}

/** An app deep link: `scheme://...`, never a script, data or file scheme. */
const APP_SCHEME = /^(?!(?:javascript|data|vbscript|file|blob):)[a-z][a-z0-9+.-]*:\/\//i;

/** Run a compiled domain-config tile action (module, link, app link, flow). */
export function runTileAction(tile: CompiledPublicTile, navigate: NavigateFunction, state?: unknown): void {
  switch (tile.action_type) {
    case 'module': {
      const route = tile.action_config.module_route ?? routeForModule(tile.module_required);
      if (route) navigate(route, { state });
      break;
    }
    case 'link':
      if (tile.action_config.link_url) {
        void openLink(tile.action_config.link_url);
      }
      break;
    case 'app_link': {
      const { app_scheme, app_package, app_store_url } = tile.action_config;
      const appUri = platform.isAndroid() ? app_package : app_scheme;
      if (appUri && APP_SCHEME.test(appUri)) {
        window.location.href = appUri;
        // Not installed: the store page opens after the scheme had its chance.
        if (app_store_url) setTimeout(() => void openLink(app_store_url), 800);
      }
      break;
    }
    case 'flow_template':
      // eForm tiles compile to `flow_template` and carry the eForm's survey template id.
      if (tile.action_config.survey_template_id) {
        navigate(`/app/eforms/${tile.action_config.survey_template_id}`, { state });
      }
      break;
  }
}
